import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Table, Form } from 'react-bootstrap';
import * as XLSX from 'xlsx-js-style';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';

import { API_BASE } from '../config';
import { scopeStations } from '../components/roleAccess';
import './buses_reports.css';
import { tr } from '../i18n';

// Fresh token read every call - never captured once and reused, same fix
// applied to Cards.js earlier (a captured token can go stale mid-session).
const authToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('token')).data.access_token;
  } catch (e) {
    return '';
  }
};

const BusesReports = ({ token }) => {
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);

  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);

  const [reportMeta, setReportMeta] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [reportGenerated, setReportGenerated] = useState(true); // false only after a confirmed 404 "not generated yet"

  const [loading, setLoading] = useState(false);

  // Archive search text - filters the past-shifts table below the main
  // report by date/time text (e.g. "9/21" or "12:14").
  const [archiveSearch, setArchiveSearch] = useState('');

  // Station list, scoped by role - same pattern as Cards.js. Every station
  // the user can otherwise see is offered here; a station that isn't
  // actually enabled for bus/route monitoring is rejected by the backend
  // with a clear message rather than being filtered out client-side, so
  // there's only one place (BUS_STATION_SERIALS in route_report.py) that
  // has to be kept up to date.
  //
  // A manager scoped to exactly one station has nothing to choose between,
  // so that single station is auto-selected instead of leaving the picker
  // empty and making them click it every time.
  const fetchStations = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/station/all`, { headers: { Authorization: authToken() } });
      const j = await r.json();
      if (j && j.data) {
        const vfdStations = j.data
          .filter((s) => s.is_vfd)
          .map((s) => ({ name: s.name, serial_number: s.serial_number }));
        const scoped = scopeStations(token, vfdStations);
        setStations(scoped);
        if (scoped.length === 1) {
          setSelectedStation(scoped[0]);
        }
      }
    } catch (err) {
      console.warn('Could not fetch stations:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  const fetchShifts = useCallback(async (stationSerial) => {
    try {
      const r = await fetch(
        `${API_BASE}/ak4tek/cards/route_report/shifts?station_serial=${encodeURIComponent(stationSerial)}`,
        { headers: { Authorization: authToken() } }
      );
      const j = await r.json();
      if (r.ok && j && j.data) {
        setShifts(j.data);
        const open = j.data.find((s) => s.is_open);
        setSelectedShift(open || j.data[0] || null);
      } else {
        setShifts([]);
        setSelectedShift(null);
        if (r.status === 403) {
          Swal.fire({ title: tr("Not enabled"), text: j.message || tr("Route/bus monitoring is not enabled for this station."), icon: 'warning' });
        }
      }
    } catch (err) {
      console.warn('Could not fetch shifts:', err);
    }
  }, []);

  useEffect(() => {
    setReportMeta(null);
    setReportRows([]);
    setReportGenerated(true);
    setArchiveSearch('');
    if (selectedStation) {
      fetchShifts(selectedStation.serial_number);
    } else {
      setShifts([]);
      setSelectedShift(null);
    }
  }, [selectedStation, fetchShifts]);

  const fetchReport = useCallback(async () => {
    if (!selectedStation || !selectedShift) return;
    try {
      const r = await fetch(
        `${API_BASE}/ak4tek/cards/route_report/report?station_serial=${encodeURIComponent(selectedStation.serial_number)}&shift_id=${selectedShift.id}`,
        { headers: { Authorization: authToken() } }
      );
      const j = await r.json();
      if (r.ok) {
        setReportMeta(j);
        setReportRows(j.children || []);
        setReportGenerated(true);
      } else if (r.status === 404) {
        setReportMeta(null);
        setReportRows([]);
        setReportGenerated(false);
      } else {
        Swal.fire({ title: tr("Error"), text: j.message || tr("Could not fetch report"), icon: 'error' });
      }
    } catch (err) {
      console.warn('Could not fetch report:', err);
    }
  }, [selectedStation, selectedShift]);

  useEffect(() => {
    if (selectedShift) {
      fetchReport();
    } else {
      setReportMeta(null);
      setReportRows([]);
    }
  }, [selectedShift, fetchReport]);

  const handleToggleShift = async () => {
    if (!selectedStation) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/ak4tek/cards/route_report/shift/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken() },
        body: JSON.stringify({ station_serial: selectedStation.serial_number }),
      });
      const j = await r.json();
      if (r.ok) {
        Swal.fire({ title: tr("Shift toggled"), icon: 'success', timer: 1400, showConfirmButton: false });
        await fetchShifts(selectedStation.serial_number);
      } else {
        Swal.fire({ title: tr("Error"), text: j.message || tr("Could not toggle shift"), icon: 'error' });
      }
    } catch (err) {
      Swal.fire({ title: tr("Error"), text: String(err), icon: 'error' });
    }
    setLoading(false);
  };

  const handleGenerateReport = async () => {
    if (!selectedStation || !selectedShift) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/ak4tek/cards/route_report/report/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken() },
        body: JSON.stringify({ station_serial: selectedStation.serial_number, shift_id: selectedShift.id }),
      });
      const j = await r.json();
      if (r.ok) {
        Swal.fire({ title: tr("Report generated"), text: tr("{0} bus(es)", j.bus_count), icon: 'success', timer: 1400, showConfirmButton: false });
        await fetchReport();
      } else {
        Swal.fire({ title: tr("Error"), text: j.message || tr("Could not generate report"), icon: 'error' });
      }
    } catch (err) {
      Swal.fire({ title: tr("Error"), text: String(err), icon: 'error' });
    }
    setLoading(false);
  };

  const handleDownloadExcel = () => {
    if (!reportRows.length) return;

    const headerStyle = {
      fill: { fgColor: { rgb: 'FF1F2937' } },
      font: { bold: true, color: { rgb: 'FFFFFFFF' } },
      alignment: { horizontal: 'center' },
    };
    const redStyle = { fill: { fgColor: { rgb: 'FFFFC7CE' } }, font: { color: { rgb: 'FF9C0006' } } };
    const greenStyle = { fill: { fgColor: { rgb: 'FFC6EFCE' } }, font: { color: { rgb: 'FF006100' } } };

    const header = [tr('Car Plate'), tr('Customer Name'), tr('Actual Sales (L)'), tr('Route (L)'), tr('Status')];
    const data = [
      header,
      ...reportRows.map((row) => [
        row.car_plate || '',
        row.customer_name || '',
        row.actual_sales,
        row.route,
        row.over_route ? 'OVER ROUTE' : 'OK',
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);

    header.forEach((_, c) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[ref]) ws[ref].s = headerStyle;
    });

    reportRows.forEach((row, i) => {
      const style = row.over_route ? redStyle : greenStyle;
      for (let c = 0; c < header.length; c++) {
        const ref = XLSX.utils.encode_cell({ r: i + 1, c });
        if (ws[ref]) ws[ref].s = style;
      }
    });

    ws['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 16 }, { wch: 12 }, { wch: 14 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Buses Report');

    const stationName = selectedStation ? selectedStation.name.replace(/[^A-Za-z0-9_-]+/g, '_') : 'station';
    const shiftLabel = selectedShift ? selectedShift.id : 'shift';
    XLSX.writeFile(wb, `buses_report_${stationName}_shift${shiftLabel}.xlsx`);
  };

  const formatShiftLabel = (s) => {
    const start = s.start_time ? new Date(s.start_time).toLocaleString() : '';
    const end = s.end_time ? new Date(s.end_time).toLocaleString() : 'ongoing';
    return `${s.is_open ? 'OPEN — ' : ''}${start} → ${end}`;
  };

  // Archive: every closed shift for this station, newest first, filtered
  // by the free-text search against its formatted date range. The open
  // shift is left out here since it already has its own row in the
  // controls above and "archive" implies a finished shift.
  const archivedShifts = useMemo(() => {
    const closed = shifts.filter((s) => !s.is_open);
    if (!archiveSearch.trim()) return closed;
    const needle = archiveSearch.trim().toLowerCase();
    return closed.filter((s) => formatShiftLabel(s).toLowerCase().includes(needle));
  }, [shifts, archiveSearch]);

  const handleViewArchivedShift = (shift) => {
    setSelectedShift(shift);
  };

  return (
    <div className="BusesReportsPage">
      <h3>{tr("Buses Reports")}</h3>

      <div className="brp-controls">
        <div className="brp-field">
          <label>{tr("Station")}</label>
          <Autocomplete
            options={stations}
            getOptionLabel={(s) => s.name || ''}
            value={selectedStation}
            onChange={(e, val) => setSelectedStation(val)}
            renderInput={(params) => (
              <TextField {...params} placeholder={tr("Select station...")} variant="outlined" size="small" />
            )}
          />
        </div>

        <Button variant="danger" disabled={!selectedStation || loading} onClick={handleToggleShift}>
          {loading ? tr("Working...") : tr("Start / End Shift")}
        </Button>

        <div className="brp-field">
          <label>{tr("Shift")}</label>
          <Form.Control
            as="select"
            value={selectedShift ? selectedShift.id : ''}
            onChange={(e) => {
              const s = shifts.find((sh) => String(sh.id) === e.target.value);
              setSelectedShift(s || null);
            }}
            disabled={!shifts.length}
          >
            {shifts.length === 0 && <option value="">{tr("No shifts yet")}</option>}
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {formatShiftLabel(s)}
              </option>
            ))}
          </Form.Control>
        </div>

        <Button variant="primary" disabled={!selectedShift || loading} onClick={handleGenerateReport}>
          {tr("Generate Report")}
        </Button>

        <Button variant="success" disabled={!reportRows.length} onClick={handleDownloadExcel}>
          {tr("Download Excel")}
        </Button>
      </div>

      {reportMeta && (
        <div className="brp-meta">
          {/* Show the station's name, not its serial number - the serial
              is still what's sent to the backend (selectedStation.serial_number),
              this only changes what's displayed. Falls back to the serial
              if the name isn't available for some reason (e.g. station list
              hasn't loaded yet but a report already has). */}
          <span>{tr("Station:")} {selectedStation ? selectedStation.name : reportMeta.station_serial_number}</span>
          <span>{tr("Start:")} {reportMeta.start_date ? new Date(reportMeta.start_date).toLocaleString() : '-'}</span>
          <span>{tr("End:")} {reportMeta.end_date ? new Date(reportMeta.end_date).toLocaleString() : tr("ongoing")}</span>
        </div>
      )}

      <Table className="brp-table" bordered>
        <thead>
          <tr>
            <th>{tr("Car Plate")}</th>
            <th>{tr("Customer Name")}</th>
            <th>{tr("Actual Sales (L)")}</th>
            <th>{tr("Route (L)")}</th>
            <th>{tr("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {reportRows.length === 0 && (
            <tr>
              <td colSpan={5} className="brp-empty">
                {!selectedStation
                  ? tr("Select a station to begin.")
                  : !selectedShift
                  ? tr("No shifts yet for this station — click \"Start / End Shift\" to open one.")
                  : !reportGenerated
                  ? tr("No report generated yet for this shift — click \"Generate Report\".")
                  : tr("No cards found for this station.")}
              </td>
            </tr>
          )}
          {reportRows.map((row, i) => (
            <tr key={i} className={row.over_route ? 'brp-row-over' : 'brp-row-ok'}>
              <td>{row.car_plate}</td>
              <td>{row.customer_name}</td>
              <td>{row.actual_sales}</td>
              <td>{row.route}</td>
              <td>{row.over_route ? tr("OVER ROUTE") : tr("OK")}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      {selectedStation && (
        <div className="brp-archive">
          <div className="brp-archive-header">
            <h4>{tr("Archive — Previous Shifts")}</h4>
            <Form.Control
              type="text"
              placeholder={tr("Search by date/time...")}
              value={archiveSearch}
              onChange={(e) => setArchiveSearch(e.target.value)}
              className="brp-archive-search"
            />
          </div>

          <Table className="brp-table" bordered size="sm">
            <thead>
              <tr>
                <th>{tr("Start")}</th>
                <th>{tr("End")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {archivedShifts.length === 0 && (
                <tr>
                  <td colSpan={3} className="brp-empty">
                    {shifts.filter((s) => !s.is_open).length === 0
                      ? tr("No closed shifts yet for this station.")
                      : tr("No shifts match that search.")}
                  </td>
                </tr>
              )}
              {archivedShifts.map((s) => (
                <tr
                  key={s.id}
                  className={selectedShift && selectedShift.id === s.id ? 'brp-row-selected' : ''}
                >
                  <td>{s.start_time ? new Date(s.start_time).toLocaleString() : '-'}</td>
                  <td>{s.end_time ? new Date(s.end_time).toLocaleString() : '-'}</td>
                  <td>
                    <Button size="sm" variant="outline-light" onClick={() => handleViewArchivedShift(s)}>
                      {tr("View")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default BusesReports;